import React, { useState } from 'react';
import { PieChart, DollarSign, Download, Info, Layers, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для отображения разбивки затрат на внедрение ИИ
 */
const CostBreakdown = ({ costBreakdown, results, showFullCostBreakdown, setShowFullCostBreakdown, formData }) => {
  // Состояние для активной вкладки
  const [activeTab, setActiveTab] = useState('chart');
  
  if (!costBreakdown || costBreakdown.length === 0 || !results) {
    return null;
  }

  // Функция для форматирования денежных значений
  const formatMoney = (value) => {
    return formatCurrency(value, 0, results.currency);
  };

  // Сортируем категории по стоимости (от наибольшей к наименьшей)
  const sortedBreakdown = [...costBreakdown].sort((a, b) => b.cost - a.cost);

  // Определяем цвета для категорий разбивки
  const categoryColors = {
    'Базовые затраты': 'bg-indigo-500 dark:bg-indigo-400',
    'Инфраструктура': 'bg-blue-500 dark:bg-blue-400',
    'Внедрение и кастомизация': 'bg-purple-500 dark:bg-purple-400',
    'Обучение персонала': 'bg-emerald-500 dark:bg-emerald-400',
    'Подготовка данных': 'bg-amber-500 dark:bg-amber-400',
    'Консалтинг': 'bg-pink-500 dark:bg-pink-400',
    'Ежегодные расходы на поддержку': 'bg-orange-500 dark:bg-orange-400'
  };

  // Получаем цвет для категории (с фолбэком)
  const getCategoryColor = (category) => {
    return categoryColors[category] || 'bg-gray-500 dark:bg-gray-400';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <PieChart className="w-5 h-5 mr-2 text-indigo-500" />
          Разбивка затрат на внедрение ИИ
        </h3>

        {/* Кнопка переключения для показа всех деталей */}
        <Button
          size="sm"
          onClick={() => setShowFullCostBreakdown(prev => !prev)}
          variant="outline"
          aria-expanded={showFullCostBreakdown}
        >
          {showFullCostBreakdown ? 'Скрыть детали' : 'Показать подробную информацию'}
        </Button>
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('chart')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'chart'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Диаграмма
        </button>
        <button
          onClick={() => setActiveTab('table')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'table'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Таблица затрат
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>Разбивка общей стоимости внедрения ИИ ({formatMoney(results.totalImplementationCost)}) 
          по основным категориям затрат. {!showFullCostBreakdown && 'Нажмите "Показать подробную информацию" для доступа к детальному анализу затрат.'}</p>
        </div>
      </div>

      {/* Отображение диаграммы */}
      {activeTab === 'chart' && (
        <div className="mb-6">
          {/* Визуальное представление разбивки затрат */}
          <div className="flex flex-col space-y-3 mb-4">
            {sortedBreakdown.map((item, index) => (
              <div key={index} className="flex flex-col">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{item.category}</span>
                  <span className="text-gray-700 dark:text-gray-300">{formatMoney(item.cost)} ({item.percentage.toFixed(1)}%)</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div 
                    className={`h-2.5 rounded-full ${getCategoryColor(item.category)}`} 
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
                {showFullCostBreakdown && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">{item.description}</p>
                )}
              </div>
            ))}
          </div>

          {/* Дополнительная информация при подробном просмотре */}
          {showFullCostBreakdown && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2 flex items-center gap-1.5">
                  <BarChart2 className="w-4 h-4 text-indigo-500" />
                  Ключевые финансовые показатели
                </h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Стоимость на студента:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{formatMoney(results.totalCostPerStudent)}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Базовая стоимость ИИ на студента:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{formatMoney(results.baseStudentCost)}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Годовые затраты на поддержку:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{formatMoney(results.annualMaintenanceCost)}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Совокупные затраты за 3 года:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{formatMoney(results.totalThreeYearCost)}</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2 flex items-center gap-1.5">
                  <Info className="w-4 h-4 text-indigo-500" />
                  Факторы, влияющие на стоимость
                </h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Количество студентов:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{formData.studentsCount}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Количество преподавателей:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{formData.facultyCount}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Уровень интеграции ИИ:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{getIntegrationLevelName(formData.aiIntegrationLevel)}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Количество ИИ-решений:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{formData.aiSolutionsCount}</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Отображение таблицы */}
      {activeTab === 'table' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 cost-breakdown-table">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">Категория затрат</th>
                <th scope="col" className="px-4 py-3 font-semibold text-right">Сумма</th>
                <th scope="col" className="px-4 py-3 font-semibold text-right">% от общей</th>
                {showFullCostBreakdown && (
                  <th scope="col" className="px-4 py-3 font-semibold">Описание</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedBreakdown.map((item, index) => (
                <tr 
                  key={index} 
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
                >
                  <td className="px-4 py-3 whitespace-nowrap font-medium flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${getCategoryColor(item.category)}`}></div>
                    {item.category}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right font-medium">{formatMoney(item.cost)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">{item.percentage.toFixed(1)}%</td>
                  {showFullCostBreakdown && (
                    <td className="px-4 py-3">{item.description}</td>
                  )}
                </tr>
              ))}
              <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                <td className="px-4 py-3">ИТОГО</td>
                <td className="px-4 py-3 text-right">{formatMoney(results.totalImplementationCost)}</td>
                <td className="px-4 py-3 text-right">100%</td>
                {showFullCostBreakdown && (
                  <td className="px-4 py-3">Общая стоимость внедрения ИИ</td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Кнопка показа полной разбивки (если она скрыта) */}
      {!showFullCostBreakdown && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullCostBreakdown(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать детальный анализ затрат
          </Button>
        </div>
      )}
    </div>
  );
};

// Получение названия уровня интеграции по ID
const getIntegrationLevelName = (id) => {
  const labels = {
    'basic': 'Базовый',
    'moderate': 'Умеренный',
    'advanced': 'Продвинутый',
    'comprehensive': 'Комплексный'
  };
  return labels[id] || id;
};

export default CostBreakdown;